<template>
  <div class="filter">
     <div class="searchbar-filter">
      <input
        id="search"
        v-model="messageSearch"
        class="message-search-bar"
        :placeholder="t('result_view.journal_analysis.searchbar_placeholder')"
      />
    </div>
    <div class="date-filter">
      <VueDatePicker
        v-model="dateRange"
        range
        :format="'yyyy-MM-dd'"
        :enable-time-picker="false"
        :dark="isDarkTheme"
        :placeholder="t('result_view.log_analysis.datepicker_placeholder')"
        class="date-range-calendar"
      />
    </div>
  </div>
  <div v-if="logNonMainEntries.length === 0">
    <div v-if="dataStore.oneFileSelected" class="no-data-screen">
      {{ t('result_view.log_analysis.no_errors_selected') }}
    </div>
    <div v-else class="no-data-screen">
      {{ t('result_view.log_analysis.no_errors') }}
    </div>
  </div>
  <div v-else>
    <TreeTable
      :value="filteredTree"
      :loading="loading"
      @toggle="onToggle"
      :expandedRows="expandedKeys"
      sort-mode="single"
      @sort="onSort"
      sort-field="timestamp"
      :sort-order="-1"
      removable-sort
      scrollable
      class="error-table"
    >
      <!-- expander + Error Code + Info Icon on children -->
      <Column
        expander
        field="errorCode"
        :header="t('result_view.log_analysis.errorcode')"
        filter
        class="entry-left-edge log-list-entry-item"
      >
        <template #body="{ node }">
          <span>{{ node.data.errorCode }}</span>
          <span
            v-if="!node.children || node.children.length === 0"
            class="info-tooltip-button"
            @click.stop="onInfoClick(node)"
          >
            <img src="@/assets/infoIcon.svg" alt="info icon" class="info-tooltip-icon">
        </span>
        </template>
      </Column>

      <!-- amount -->
      <Column
        field="count"
        :header="t('result_view.log_analysis.amount')"
        sortable
        filter
        class="log-list-entry-item"
      />

      <!-- Date/Time -->
      <Column
        field="timestamp"
        :header="t('result_view.log_analysis.date_time')"
        sortable
        filter
        class="log-list-entry-item"
      />

      <!-- message -->
      <Column
        field="Value"
        :header="t('result_view.log_analysis.message')"
        filter
        class="entry-right-edge log-list-entry-item"
        style="word-wrap: break-word;"
      />
    </TreeTable>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useDataStore } from '@/stores/dataStore.js'
import { useI18n } from 'vue-i18n'
import TreeTable from 'primevue/treetable'
import Column    from 'primevue/column'
import VueDatePicker from '@vuepic/vue-datepicker'

const { t } = useI18n()
const dataStore = useDataStore()
const loading = ref(false)
const expandedKeys = ref({})
const dateRange = ref(null)
const sortField = ref('timestamp')
const sortOrder = ref(-1)
const messageSearch = ref('')

const theme = ref(localStorage.getItem('user-theme') || 'light-theme')
const isDarkTheme = computed(() => theme.value === 'dark-theme')

// Build tree data with filtering
const filteredTree = computed(() => {
  return Object.entries(dataStore.logErrors || {}).map(([errorCode, entries]) => {
    const filteredChildren = entries
      .map((detail, i) => ({
        key: `${errorCode}-${i}`,
        data: {
          errorCode: '',
          timestamp: detail.timestamp || '…',
          count: '',
          Value: detail.Value || '…',
          Lines: detail.Lines,
          zebra: i % 2 === 1
        }
      }))
      .filter(child => isDateInRange(child.data.timestamp))
      .filter(child =>
        !messageSearch.value ||
        child.data.Value.toLowerCase().includes(messageSearch.value.toLowerCase())
      )
      .sort((a, b) => {
        if (['timestamp', 'Value'].includes(sortField.value)) {
          const aValue = a.data[sortField.value] || ''
          const bValue = b.data[sortField.value] || ''
          if (aValue === bValue) return 0
          return (aValue > bValue ? 1 : -1) * sortOrder.value
        }
        return 0
      })

    return {
      key: errorCode,
      data: {
        errorCode,
        timestamp: '',
        count: `${filteredChildren.length} Errors`,
        Value: ''
      },
      children: filteredChildren
    }
  }).filter(parent => parent.children.length > 0)
})

const onSort = e => {
  // If Date, Time column is being "unsorted", force descending
  if (e.sortField === 'timestamp' && e.sortOrder === 0) {
    sortField.value = 'timestamp'
    sortOrder.value = -1
  } else {
    sortField.value = e.sortField
    sortOrder.value = e.sortOrder
  }
}

const onToggle = e => {
  expandedKeys.value = e.value
}

const emit = defineEmits(['open-modal'])
const onInfoClick = (node) => {
  emit('open-modal', node)
}

const generateErrorList = (errorData) => {
  let array = []
  for (let key in errorData) {
    array.push({ errorCode: key, count: errorData[key].length, main: true })
    for (let detailEntry of errorData[key]) {
      array.push({ ...detailEntry, errorCode: key })
    }
  }
  return array
}

const generateNonMainEntries = (entries) => {
  let result = []
  for (let i = 0; i < entries.length; i++) {
    let entry = entries[i]
    result.push(entry)
  }
  return result
}

const getLogErrors = computed(() => generateErrorList(dataStore.logErrors))
const logNonMainEntries = computed(() => generateNonMainEntries(getLogErrors.value))

onMounted(() => {
  // This sets the sorting of Date, Time by default to descending order and not on removable sorts default state
  onSort({ sortField: 'timestamp', sortOrder: -1 })

  window.addEventListener('theme-changed', syncTheme)
  window.addEventListener('storage', syncTheme)
})

onUnmounted(() => {
  window.removeEventListener('theme-changed', syncTheme)
  window.removeEventListener('storage', syncTheme)
})

function syncTheme() {
  theme.value = localStorage.getItem('user-theme') || 'light-theme'
}

// Helper: Parse "YYYY-MM-DD, ..." to Date
function parseDateFromTimestamp(ts) {
  if (!ts) return null
  const dateOnly = ts.split(',')[0].trim()
  const [y, m, d] = dateOnly.split('-')
  if (!y || !m || !d) return null
  const date = new Date(+y, +m - 1, +d)
  return isNaN(date) ? null : date
}

// Helper: Check if date is in range
function isDateInRange(ts) {
  const entryDate = parseDateFromTimestamp(ts)
  if (!entryDate) return false
  if (!dateRange.value) return true
  const [start, end] = dateRange.value
  if (start instanceof Date && !end) {
    return entryDate.toDateString() === start.toDateString()
  }
  if (start instanceof Date && end instanceof Date) {
    const s = new Date(start.getFullYear(), start.getMonth(), start.getDate())
    const e = new Date(end.getFullYear(), end.getMonth(), end.getDate())
    return entryDate >= s && entryDate <= e
  }
  return true
}
</script>

<style>
.p-treetable-table {
  padding: 20px;
  display: flex;
  flex-direction: column;
}

.p-treetable-tbody {
  display: flex;
  flex-direction: column;
}

.p-treetable-scrollable > .p-treetable-table-container > .p-treetable-table > .p-treetable-thead {
  background-color: var(--background-color-secondary) !important;
}

.p-treetable-thead > tr {
  display: flex;
}

.p-treetable-thead > tr > th {
  background-color: var(--background-color-secondary) !important;
  color: var(--text-primary-color);
}

.p-treetable .p-treetable-thead > tr > th,
.p-treetable .p-treetable-tbody > tr > td {
  padding: 5px;
  border: none;
}

.p-treetable .p-treetable-tbody > tr {
  color: var(--text-primary-color);
  display: flex;
  background-color: transparent;
  border-radius: 4px;
  align-items: center;
  overflow: hidden;
}

.p-treetable-tbody > tr[aria-level="1"] {
  background: var(--list-red-background-color) !important;
  padding: 5px 0;
  margin-top: 10px;
}

.p-treetable-tbody > tr[aria-level="1"] + tr[aria-level="2"] {
  margin-top: 5px;
}

.p-treetable-tbody tr[aria-level="2"]:nth-of-type(even) {
  background-color: var(--light-grey-background-color) !important;
}

/* Hide expander icon on child elements therefore info icon is in its place */
.p-treetable-tbody > tr[aria-level="2"] .p-treetable-node-toggle-button {
  display: none !important;
}

.no-data-screen {
  font-size: 20px;
  text-align: center;
  margin-top: 20px;
}

.log-list-entry {
  display: none;
  border-radius: 4px;
  gap: 15px;
  font-size: 15px;
  font-weight: 400;
  padding: 5px 20px;
}


.log-list-entry-main {
  font-size: 15px;
  font-weight: 400;
  line-height: 42px;
  background: var(--list-red-background-color);
  border-radius: 4px;
  cursor: pointer;
  padding: 0 20px;
  position: sticky;
  top: 0;
  z-index: 1;
}

.log-list-entry-item {
  width: 15%;
}

.entry-left-edge {
  border-radius: 4px 0px 0px 4px;
  min-width: 230px;
}

.entry-right-edge {
  border-radius: 0px 4px 4px 0px;
  flex-grow:1;
  word-wrap: break-word;
}

.info-tooltip-button {
  padding: 0;
  background-color: transparent;
  border: none;
  cursor: pointer;
}

.info-tooltip-button:hover {
  background: transparent;
  border: none;
}

.info-tooltip-icon {
  height: 20px;
  width: 20px;
}

.tab-view::-webkit-scrollbar {
  width: 10px;
}

.tab-view::-webkit-scrollbar-track {
  background-color: var(--background-color-secondary);
  border-radius: 10px 10px 0 0;
}

.tab-view::-webkit-scrollbar-thumb {
  background-color: var(--scrollbar-thumb);
  border-radius: 10px;
}

.tab-view::-webkit-scrollbar-thumb:hover {
  background-color: var(--scrollbar-thumb-hover);
}
</style>